import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getFreshBoardStock, getFreshBoardPricing } from '../../data/mockApi';

const { FiTrendingUp, FiTrendingDown, FiRefreshCw, FiGlobe, FiDatabase } = FiIcons;

// Dark theme status styles
const STATUS_COLORS = {
  healthy: {
    badge: 'bg-lime-500/10 text-lime-400 border-lime-500/30',
    bar: 'from-lime-500 to-lime-400'
  },
  low: {
    badge: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    bar: 'from-amber-500 to-amber-400'
  },
  critical: {
    badge: 'bg-red-500/10 text-red-400 border-red-500/30',
    bar: 'from-red-500 to-red-400'
  }
};

const FreshBoard = () => {
  const [activeTab, setActiveTab] = useState('stock');
  const [stockData, setStockData] = useState([]);
  const [pricingData, setPricingData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [stock, pricing] = await Promise.all([
        getFreshBoardStock(),
        getFreshBoardPricing(),
      ]);
      setStockData(stock);
      setPricingData(pricing);
      setLoading(false);
    };
    fetchData();
  }, []);

  const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.healthy;

  const getStockPercentage = (current, min, max) => {
    if (max === min) return 100;
    return Math.max(0, Math.min(((current - min) / (max - min)) * 100, 100));
  };

  const tabs = [
    { id: 'stock', label: 'Stock Levels', count: stockData.length, icon: FiDatabase },
    { id: 'pricing', label: 'Daily Pricing', count: pricingData.length, icon: FiIcons.FiDollarSign },
    { id: 'sourcing', label: 'Sourcing Ops', count: 12, icon: FiGlobe }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-[#111916] rounded-xl border border-[#243129] overflow-hidden"
    >
      {/* Header */}
      <div className="border-b border-[#243129] p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold text-[#e8f5e9]">
              Live Fresh Board
            </h2>
            <p className="text-xs font-mono text-[#6b8772] mt-1">Real-time inventory and pricing intelligence</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 bg-[#1a2420] border border-[#243129] text-[#84cc16] px-4 py-2 rounded-lg hover:border-[#84cc16]/50 transition-colors"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
            </motion.div>
            <span className="text-sm font-mono font-medium">Sync</span>
          </motion.button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 bg-[#0a0f0d] p-1 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-md text-sm font-mono transition-all duration-200 ${
                activeTab === tab.id
                  ? 'text-[#84cc16]'
                  : 'text-[#6b8772] hover:text-[#a5c4ad]'
              }`}
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="activeTabBg"
                  className="absolute inset-0 bg-[#1a2420] rounded-md"
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
              <SafeIcon icon={tab.icon} className="w-4 h-4 relative z-10" />
              <span className="relative z-10">{tab.label}</span>
              <span
                className={`relative z-10 px-1.5 py-0.5 text-xs rounded ${
                  activeTab === tab.id ? 'bg-lime-500/20 text-lime-400' : 'bg-[#1a2420] text-[#6b8772]'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <AnimatePresence mode="wait">
          {activeTab === 'stock' && (
            <motion.div
              key="stock"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-3"
            >
              {stockData.map((item, index) => {
                const statusColor = getStatusColor(item.status);
                return (
                  <motion.div
                    key={item.sku}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors group"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 text-[10px] font-mono font-medium rounded border uppercase tracking-wider ${statusColor.badge}`}>
                          {item.status}
                        </span>
                        <div>
                          <h4 className="font-mono font-medium text-[#e8f5e9] group-hover:text-[#84cc16] transition-colors">{item.name}</h4>
                          <p className="text-xs font-mono text-[#4a5f50]">{item.sku}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-display font-semibold text-[#e8f5e9] tabular-nums">{item.currentStock} <span className="text-xs font-mono text-[#6b8772]">units</span></p>
                        <p className="text-xs font-mono text-[#6b8772]">Margin: <span className="text-lime-400">{item.margin}%</span></p>
                      </div>
                    </div>

                    {/* Stock level bar */}
                    <div className="relative">
                      <div className="w-full bg-[#1a2420] rounded-full h-1.5 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(getStockPercentage(item.currentStock, item.minThreshold, item.maxThreshold), 100)}%` }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          className={`h-1.5 rounded-full bg-gradient-to-r ${statusColor.bar}`}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] font-mono text-[#4a5f50] mt-1">
                        <span>Min: {item.minThreshold}</span>
                        <span>Max: {item.maxThreshold}</span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {activeTab === 'pricing' && (
            <motion.div
              key="pricing"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-3"
            >
              {pricingData.map((item, index) => (
                <motion.div
                  key={item.sku}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-mono font-medium text-[#e8f5e9]">{item.name}</h4>
                      <p className="text-xs font-mono text-[#4a5f50]">{item.sku}</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-[10px] font-mono text-[#4a5f50] uppercase tracking-wider">Sell</p>
                        <p className="font-mono font-semibold text-[#e8f5e9] tabular-nums">R{item.sellPrice}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] font-mono text-[#4a5f50] uppercase tracking-wider">Cost</p>
                        <p className="font-mono font-semibold text-[#a5c4ad] tabular-nums">R{item.costPrice}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] font-mono text-[#4a5f50] uppercase tracking-wider">Margin</p>
                        <p className="font-mono font-semibold text-lime-400 tabular-nums">{item.margin}%</p>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded ${item.trend === 'up' ? 'bg-lime-500/10' : 'bg-red-500/10'}`}>
                        <SafeIcon
                          icon={item.trend === 'up' ? FiTrendingUp : FiTrendingDown}
                          className={`w-3.5 h-3.5 ${item.trend === 'up' ? 'text-lime-400' : 'text-red-400'}`}
                        />
                        <span className={`text-xs font-mono font-medium ${item.trend === 'up' ? 'text-lime-400' : 'text-red-400'}`}>
                          {item.priceChange}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === 'sourcing' && (
            <motion.div
              key="sourcing"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="text-center py-12"
            >
              <div className="w-16 h-16 bg-[#1a2420] border border-[#243129] rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiGlobe} className="w-8 h-8 text-[#6b8772]" />
              </div>
              <h3 className="font-display text-lg font-semibold text-[#e8f5e9] mb-2">Sourcing Opportunities</h3>
              <p className="text-sm font-mono text-[#6b8772] mb-6">International sourcing intelligence and RFQ management</p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] px-6 py-2.5 rounded-lg font-mono font-medium shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-shadow"
              >
                View Opportunities
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default FreshBoard;
